/**
 * useStats — dynamic curriculum stats hook.
 *
 * Fetches live counts from GET /api/stats so we never need to manually
 * update numbers on HomePage / AboutPage / MethodologyPage / PricingPage
 * when new content is added.
 *
 * Returns { tracks, lessons, tests, certificates, stages_per_lesson, loading }.
 * Uses sensible fallbacks matching the current curriculum size until the
 * first fetch completes, so SSR/first paint doesn't show zeros.
 */
import { useEffect, useState } from "react";
import { API } from "./api";
import { authHeaders } from "@/lib/session";

const FALLBACK = {
  tracks: 11,       // 11 tracks defined in tracks_data.py (balagha-1..6, quran, lit, archive, hamza-prep, skills)
  lessons: 154,     // live /api/stats value as of Oct 2026 — bumped from 104 which was stale and shipped in beta build 10
  tests: 47,        // live /api/stats value — bumped from 41
  certificates: 11, // one certificate per track
  stages_per_lesson: 10,
};

export function useStats() {
  const [stats, setStats] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/stats`, { credentials: "include", headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setStats({ ...FALLBACK, ...data });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...stats, loading };
}

export default useStats;
